import { useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { Channel, ForumThread } from "@atrium/shared";
import { api, errorMessage } from "../../lib/api";
import { useMe } from "../auth/AuthProvider";
import { confirm } from "../../components/ui/Dialog";
import { toast } from "../../components/ui/Toast";
import {
  ContextMenu,
  ContextMenuCheckboxItem,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from "../../components/ui/ContextMenu";
import { IconArrowRight, IconEdit, IconIdCard, IconLink, IconLock, IconPin, IconPinFilled, IconTag, IconTrash, IconUnlock } from "../../components/icons";
import { copyWithToast } from "../chat/MessageMenus";
import { PostDialog, forumPerms } from "./ForumView";
import { threadKey, threadsKey } from "./hooks";

/* Actions on a forum post, shared by the open post's "⋯" menu and the right-click menu. */

const TAG_LIMIT = 5;

export function useThreadActions(channel: Channel, isOwner: boolean, thread: ForumThread | undefined) {
  const me = useMe();
  const qc = useQueryClient();
  const fp = forumPerms(channel, isOwner);
  const isAuthor = thread?.author?.id === me.id;

  const patch = async (body: Partial<Pick<ForumThread, "pinned" | "locked" | "tagIds">>) => {
    if (!thread) return;
    try {
      const updated = await api.patch<ForumThread>(`/api/threads/${thread.id}`, body);
      qc.setQueryData(threadKey(thread.id), updated);
      void qc.invalidateQueries({ queryKey: threadsKey(channel.id) });
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  return {
    manage: fp.manage,
    canEdit: !!thread && (fp.manage || (isAuthor && !thread.locked)),
    canDelete: !!thread && (fp.manage || isAuthor),
    update: (body: Partial<Pick<ForumThread, "pinned" | "locked">>) => patch(body),
    toggleTag: (tagId: string) => {
      if (!thread) return;
      const on = thread.tagIds.includes(tagId);
      if (!on && thread.tagIds.length >= TAG_LIMIT) return void toast.error(`A post can have up to ${TAG_LIMIT} tags`);
      void patch({ tagIds: on ? thread.tagIds.filter((x) => x !== tagId) : [...thread.tagIds, tagId] });
    },
    /** Asks first; resolves true once deleted. */
    remove: async () => {
      if (!thread) return false;
      const ok = await confirm({ title: "Delete this post?", description: "The post and every reply in it will be deleted. This can't be undone.", confirmLabel: "Delete post", danger: true });
      if (!ok) return false;
      try {
        await api.del(`/api/threads/${thread.id}`);
        void qc.invalidateQueries({ queryKey: threadsKey(channel.id) });
        return true;
      } catch (e) {
        toast.error(errorMessage(e));
        return false;
      }
    },
  };
}

/**
 * Right-click / long-press on a forum post (in the list, the gallery, or the open
 * post's header): open, rename, tags, pin, lock, copy and delete.
 */
export function ThreadContextMenu({
  thread,
  channel,
  communityId,
  isOwner,
  onOpen,
  onEdit,
  onDeleted,
  children,
}: {
  thread: ForumThread;
  channel: Channel;
  communityId: string;
  isOwner: boolean;
  onOpen?: () => void;
  /** Opens an existing edit dialog; without it the menu shows its own. */
  onEdit?: () => void;
  onDeleted?: () => void;
  children: ReactNode;
}) {
  const a = useThreadActions(channel, isOwner, thread);
  const [editing, setEditing] = useState(false);
  const tags = channel.forumTags;
  const full = thread.tagIds.length >= TAG_LIMIT;
  return (
    <>
      <ContextMenu modal={false}>
        <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
        <ContextMenuContent>
          {onOpen && (
            <ContextMenuItem icon={IconArrowRight} onSelect={onOpen}>
              Open post
            </ContextMenuItem>
          )}
          {a.canEdit && (
            <ContextMenuItem icon={IconEdit} onSelect={() => (onEdit ? onEdit() : setEditing(true))}>
              Rename
            </ContextMenuItem>
          )}
          {a.canEdit && tags.length > 0 && (
            <ContextMenuSub>
              <ContextMenuSubTrigger icon={IconTag}>Tags</ContextMenuSubTrigger>
              <ContextMenuSubContent>
                {tags.map((t) => {
                  const on = thread.tagIds.includes(t.id);
                  return (
                    <ContextMenuCheckboxItem key={t.id} checked={on} onCheckedChange={() => a.toggleTag(t.id)} disabled={!on && full}>
                      {t.emoji ? `${t.emoji} ${t.name}` : t.name}
                    </ContextMenuCheckboxItem>
                  );
                })}
              </ContextMenuSubContent>
            </ContextMenuSub>
          )}
          {a.manage && (
            <>
              <ContextMenuItem icon={thread.pinned ? IconPinFilled : IconPin} onSelect={() => void a.update({ pinned: !thread.pinned })}>
                {thread.pinned ? "Unpin" : "Pin"}
              </ContextMenuItem>
              <ContextMenuItem icon={thread.locked ? IconUnlock : IconLock} onSelect={() => void a.update({ locked: !thread.locked })}>
                {thread.locked ? "Unlock" : "Lock"}
              </ContextMenuItem>
            </>
          )}
          {(onOpen || a.canEdit || a.manage) && <ContextMenuSeparator />}
          <ContextMenuItem icon={IconLink} onSelect={() => void copyWithToast(`${window.location.origin}/c/${communityId}/${channel.id}/t/${thread.id}`, "Link copied")}>
            Copy link
          </ContextMenuItem>
          <ContextMenuItem icon={IconIdCard} onSelect={() => void copyWithToast(thread.id)}>
            Copy post ID
          </ContextMenuItem>
          {a.canDelete && (
            <>
              <ContextMenuSeparator />
              <ContextMenuItem icon={IconTrash} danger onSelect={() => void a.remove().then((ok) => ok && onDeleted?.())}>
                Delete post
              </ContextMenuItem>
            </>
          )}
        </ContextMenuContent>
      </ContextMenu>
      {!onEdit && editing && <PostDialog open onOpenChange={setEditing} channel={channel} communityId={communityId} thread={thread} />}
    </>
  );
}
